import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CONFIG_SECTIONS } from "config/adminConfig";
import { isLiriAdmin, parseJwtPayload } from "util/moduleAccess";
import { getSectionIcon } from "./sectionMeta";

/**
 * Admin Configuration landing — a grid of module cards. Each card opens that
 * module's settings page (/admin/admin-config/:sectionKey). Pure navigation, no
 * data fetching here, so the landing is instant.
 */
const AdminConfig: React.FC = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("accessToken");
  const payload = parseJwtPayload(token);

  let userEmail: string | null = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) userEmail = JSON.parse(userStr)?.email ?? null;
  } catch (error) {
    console.error("Failed to parse user from local storage", error);
  }

  const isLiriAdminUser = isLiriAdmin(payload, undefined, userEmail);

  const visibleSections = useMemo(
    () =>
      CONFIG_SECTIONS.filter((section) =>
        section.key === "liriadmin" ? isLiriAdminUser : true,
      ),
    [isLiriAdminUser],
  );

  return (
    <div className="h-full w-full px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto text-slate-900 dark:text-white">
      <header className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Admin Configuration</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage company-wide settings and policies for Liriworks.
        </p>
      </header>

      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
        Modules &amp; settings
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleSections.map((section) => {
          const Icon = getSectionIcon(section.key);
          return (
            <button
              key={section.key}
              type="button"
              onClick={() => navigate(`/admin/admin-config/${section.key}`)}
              aria-label={`Manage ${section.label} settings`}
              className="group text-left rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 mb-4">
                <Icon size={22} aria-hidden="true" />
              </span>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {section.label}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {section.description}
                </p>
              )}
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400">
                Manage settings
                <ArrowRight
                  size={15}
                  className="transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdminConfig;
